import { useQuery } from '@tanstack/react-query';

import { getUserPresence, UserPresenceMap } from '@/services/api/users/api';

const PRESENCE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches lastActiveAt timestamps for a list of user IDs and determines
 * which ones are currently online (active within the last 5 minutes).
 *
 * Refetches every 60 seconds so presence stays reasonably fresh.
 */
export const useUserPresence = (userIds: string[]) => {
  // Deduplicate and sort for stable query key
  const dedupedIds = [...new Set(userIds.filter(Boolean))].sort();

  const { data: presenceMap = {} } = useQuery<UserPresenceMap>({
    queryKey: ['user-presence', dedupedIds],
    queryFn: () => getUserPresence(dedupedIds),
    enabled: dedupedIds.length > 0,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const isOnline = (userId: string): boolean => {
    const ts = presenceMap[userId];
    if (!ts) return false;
    const parsed = new Date(ts).getTime();
    if (Number.isNaN(parsed)) return false;
    return Date.now() - parsed < PRESENCE_TIMEOUT_MS;
  };

  const getLastActiveAt = (userId: string): string | undefined => presenceMap[userId] ?? undefined;

  return { presenceMap, isOnline, getLastActiveAt };
};
